"use client";

import { faArrowUp, faComment, faRobot, faXmark } from "@fortawesome/pro-solid-svg-icons";
import { useLocale, useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";
import { Icon } from "@/components/ui/Icon";

interface Msg {
	role: "user" | "assistant";
	content: string;
}

// Turn /products/<slug> paths in the reply into clickable links.
function renderContent(text: string, locale: string) {
	const parts = text.split(/(\/products\/[a-z0-9-]+)/gi);
	return parts.map((part, i) =>
		/^\/products\/[a-z0-9-]+$/i.test(part) ? (
			<a key={i} href={`/${locale}${part}`} className="text-primary underline underline-offset-2">
				{part}
			</a>
		) : (
			<span key={i}>{part}</span>
		),
	);
}

// Conversation survives page navigation / reload within the browser session.
const STORAGE_KEY = "jana-ai-chat";

function loadStored(): { open: boolean; messages: Msg[] } | null {
	try {
		const raw = sessionStorage.getItem(STORAGE_KEY);
		if (!raw) return null;
		const parsed = JSON.parse(raw) as { open?: boolean; messages?: Msg[] };
		return {
			open: Boolean(parsed.open),
			messages: Array.isArray(parsed.messages) ? parsed.messages : [],
		};
	} catch {
		return null;
	}
}

export function AiChat({ welcome }: { welcome: string }) {
	const t = useTranslations("shop");
	const locale = useLocale();
	const [open, setOpen] = useState(false);
	const [messages, setMessages] = useState<Msg[]>([]);
	const [input, setInput] = useState("");
	const [loading, setLoading] = useState(false);
	const [hydrated, setHydrated] = useState(false);
	const scrollRef = useRef<HTMLDivElement>(null);

	// Restore the conversation after mount (avoids SSR hydration mismatch).
	useEffect(() => {
		const stored = loadStored();
		if (stored) {
			setMessages(stored.messages);
			setOpen(stored.open);
		}
		setHydrated(true);
	}, []);

	// Persist on every change so navigation / reload keeps the conversation.
	useEffect(() => {
		if (!hydrated) return;
		try {
			sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ open, messages: messages.slice(-30) }));
		} catch {
			/* storage full / unavailable — ignore */
		}
	}, [open, messages, hydrated]);

	useEffect(() => {
		scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
	}, [messages, loading, open]);

	async function send() {
		const text = input.trim();
		if (!text || loading) return;
		const next = [...messages, { role: "user" as const, content: text }];
		setMessages(next);
		setInput("");
		setLoading(true);
		try {
			const res = await fetch("/api/ai/chat", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ messages: next, locale }),
			});
			const data = await res.json();
			setMessages((m) => [
				...m,
				{
					role: "assistant",
					content: res.ok && data.reply ? data.reply : t("aiError"),
				},
			]);
		} catch {
			setMessages((m) => [...m, { role: "assistant", content: t("aiError") }]);
		} finally {
			setLoading(false);
		}
	}

	return (
		<>
			{/* Launcher */}
			<button
				type="button"
				onClick={() => setOpen((v) => !v)}
				aria-label={t("aiTitle")}
				className="fixed bottom-5 end-5 z-40 grid h-14 w-14 place-items-center rounded-full shadow-lg transition hover:scale-105"
				style={{ background: "var(--primary)", color: "var(--primary-foreground)" }}
			>
				<Icon icon={open ? faXmark : faComment} size={22} />
			</button>

			{/* Panel */}
			{open && (
				<div className="fixed bottom-24 end-5 z-40 flex h-[28rem] w-[22rem] max-w-[calc(100vw-2.5rem)] flex-col overflow-hidden rounded-2xl border border-border bg-bg shadow-[var(--shadow)]">
					<div
						className="flex items-center gap-2 px-4 py-3"
						style={{ background: "var(--primary)", color: "var(--primary-foreground)" }}
					>
						<Icon icon={faRobot} size={18} />
						<span className="text-sm font-semibold">{t("aiTitle")}</span>
					</div>

					<div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto p-4 text-sm">
						<div className="flex">
							<div className="max-w-[85%] rounded-2xl bg-surface-2 px-3 py-2">
								{welcome || t("aiWelcome")}
							</div>
						</div>
						{messages.map((m, i) => (
							<div key={i} className={`flex ${m.role === "user" ? "justify-end" : ""}`}>
								<div
									className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-3 py-2 ${
										m.role === "user" ? "bg-primary text-primary-foreground" : "bg-surface-2"
									}`}
								>
									{m.role === "assistant" ? renderContent(m.content, locale) : m.content}
								</div>
							</div>
						))}
						{loading && (
							<div className="flex">
								<div className="rounded-2xl bg-surface-2 px-3 py-2 text-muted-foreground">…</div>
							</div>
						)}
					</div>

					<form
						onSubmit={(e) => {
							e.preventDefault();
							// `send` is fully self-handling (see its try/catch/finally);
							// the void marks the deliberate non-await.
							void send();
						}}
						className="flex items-center gap-2 border-t border-border p-2"
					>
						<input
							value={input}
							onChange={(e) => setInput(e.target.value)}
							placeholder={t("aiPlaceholder")}
							className="input flex-1 text-sm"
						/>
						<button
							type="submit"
							disabled={loading || !input.trim()}
							className="btn btn-primary px-3 py-2 text-sm disabled:opacity-50"
						>
							<Icon icon={faArrowUp} size={14} />
						</button>
					</form>
				</div>
			)}
		</>
	);
}
